import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import {
  ClippingStudioSubtitleStyle,
  SUBTITLE_BOX_PADDING,
  SUBTITLE_LINE_HEIGHT_RATIO,
  SUBTITLE_SHADOW_BLUR,
  SUBTITLE_SHADOW_COLOR,
  SUBTITLE_SHADOW_OFFSET_X,
  SUBTITLE_SHADOW_OFFSET_Y,
} from "app/js/src/domain/clipping-studio/clipping-studio-transcript";
import { ClippingStudioFontService } from "app/js/src/infrastructure/clipping-studio/clipping-studio-font.service";

@Component({
  selector: "evrideo-clipping-studio-subtitle-style-panel",
  templateUrl: "./clipping-studio-subtitle-style-panel.component.html",
  styleUrls: ["./clipping-studio-subtitle-style-panel.component.scss"],
})
export class ClippingStudioSubtitleStylePanelComponent
  implements OnInit, AfterViewInit
{
  /** Current project style when the panel opens (null → panel defaults). */
  @Input() initialStyle: ClippingStudioSubtitleStyle | null = null;

  @Output() close = new EventEmitter<void>();
  @Output() applyStyle = new EventEmitter<ClippingStudioSubtitleStyle>();

  private static readonly DEFAULT_FONTS = [
    "Open Sans",
    "Roboto",
    "Montserrat",
    "Lato",
    "Inter",
  ];

  activeFont = "Open Sans";
  /**
   * Font the sample is drawn with. Trails `activeFont` until the new face is
   * loaded, so switching fonts never flashes the system fallback.
   */
  previewFont = "Open Sans";
  fontSize = 24;
  /** Text color: one of theme neutral/primary values (rgba strings from _theme.scss). */
  fontColor = "rgba(255, 255, 255, 1)";
  bgColor = "rgba(0,0,0,0.8)";
  position: "top" | "middle" | "bottom" = "bottom";
  /** "solid" = black box (default), "dropShadow" = drop shadow only. */
  backgroundStyle: "solid" | "outline" | "dropShadow" = "solid";

  fonts: string[] = ClippingStudioSubtitleStylePanelComponent.DEFAULT_FONTS;
  /** True when the active font file failed to load for preview. */
  fontLoadFailed = false;
  readonly fontLoadErrorTitle = "Font style could not be loaded.";
  readonly fontErrorIcon = "/assets/icons/pending.svg";
  /** Theme colors from _theme.scss: white, black, purple, red, yellow, green. */
  readonly colors = [
    "rgba(255, 255, 255, 1)",
    "rgba(0, 0, 0, 1)",
    "rgba(158, 77, 244, 1)",
    "rgba(239, 93, 93, 1)",
    "rgba(244, 169, 60, 1)",
    "rgba(85, 146, 142, 1)",
  ];
  /** Font size options 12–48px (same range as previous slider). */
  readonly fontSizeOptions = Array.from({ length: 37 }, (_, i) => 12 + i);

  @ViewChild("previewScroll") previewScrollRef?: ElementRef<HTMLDivElement>;
  @ViewChild("previewText") previewTextRef?: ElementRef<HTMLDivElement>;

  /** Short enough to fit the field at any selectable size. */
  private static readonly PREVIEW_DEFAULT_TEXT = "Type your text";
  /** Sample text shown in the style field; local to the panel, never persisted. */
  previewText = ClippingStudioSubtitleStylePanelComponent.PREVIEW_DEFAULT_TEXT;
  /** Keeps the sample from turning the field into an endless strip. */
  private static readonly PREVIEW_MAX_LENGTH = 40;
  /** Until the user types, focusing the field selects the default sample. */
  private previewEdited = false;

  private fontEnsureToken = 0;

  constructor(
    private readonly fontService: ClippingStudioFontService,
    private readonly cdr: ChangeDetectorRef,
  ) {
    const tenantFontNames = this.fontService.getAvailableFontNames();
    if (tenantFontNames.length > 0) {
      this.fonts = tenantFontNames;
    }
  }

  ngOnInit(): void {
    const style = this.initialStyle;
    if (style) {
      this.activeFont = style.fontFamily;
      this.fontSize = style.fontSize;
      this.fontColor = style.fontColor;
      this.bgColor = style.backgroundColor;
      this.backgroundStyle =
        style.backgroundStyle === "outline" ? "solid" : style.backgroundStyle;
      this.position = style.position;
    }
    this.previewFont = this.activeFont;
    void this.refreshActiveFontLoad();
  }

  ngAfterViewInit(): void {
    const el = this.previewTextRef?.nativeElement;
    if (el) {
      el.textContent = this.previewText;
    }
  }

  onFontChange(font: string): void {
    this.activeFont = font;
    void this.applyPreviewFontAfterLoad(font);
  }

  /**
   * Inline style for the live sample. Reproduces the canvas renderer: same
   * line height, same plate padding, same shadow. Empty field stays unstyled so
   * the placeholder is readable at any chosen font size.
   */
  get previewTextStyle(): Record<string, string> {
    if (!this.previewText) {
      return {};
    }
    return {
      "font-family": this.fontService.getCssFamily(this.previewFont),
      "font-size": `${this.fontSize}px`,
      "line-height": `${SUBTITLE_LINE_HEIGHT_RATIO}`,
      color: this.fontColor,
      padding: `${SUBTITLE_BOX_PADDING / 2}px ${SUBTITLE_BOX_PADDING}px`,
      "background-color":
        this.backgroundStyle === "solid" ? this.bgColor : "transparent",
      "text-shadow":
        this.backgroundStyle === "dropShadow"
          ? `${SUBTITLE_SHADOW_OFFSET_X}px ${SUBTITLE_SHADOW_OFFSET_Y}px ${SUBTITLE_SHADOW_BLUR}px ${SUBTITLE_SHADOW_COLOR}`
          : "none",
    };
  }

  onPreviewInput(): void {
    const el = this.previewTextRef?.nativeElement;
    if (!el) {
      return;
    }
    const raw = el.textContent ?? "";
    const sanitized = this.sanitizePreviewText(raw);
    // Rewriting the node resets the caret, so only do it when input was altered.
    if (sanitized !== raw) {
      const caret = this.getCaretOffset(el);
      el.textContent = sanitized;
      this.setCaretOffset(el, Math.min(caret, sanitized.length));
    }
    // Deleting the last character leaves a stray <br> behind in some browsers,
    // which would push the placeholder onto a second line.
    if (!sanitized && el.innerHTML) {
      el.innerHTML = "";
    }
    this.previewEdited = true;
    this.previewText = sanitized;
    this.scrollCaretIntoView();
  }

  /** First focus selects the default sample so typing replaces it outright. */
  onPreviewFocus(): void {
    const el = this.previewTextRef?.nativeElement;
    const selection = window.getSelection();
    if (this.previewEdited || !el || !selection) {
      return;
    }
    const range = document.createRange();
    range.selectNodeContents(el);
    selection.removeAllRanges();
    selection.addRange(range);
  }

  /** Sample sits centred when idle; while typing it follows the caret instead. */
  onPreviewBlur(): void {
    const scroll = this.previewScrollRef?.nativeElement;
    if (!scroll) {
      return;
    }
    scroll.scrollLeft = Math.max(
      0,
      (scroll.scrollWidth - scroll.clientWidth) / 2,
    );
  }

  focusPreview(): void {
    this.previewTextRef?.nativeElement.focus();
  }

  onClose(): void {
    this.close.emit();
  }

  onApply(): void {
    void this.applyAfterFontLoad();
  }

  private async applyAfterFontLoad(): Promise<void> {
    const token = ++this.fontEnsureToken;
    this.fontLoadFailed = false;
    const result = await this.fontService.ensureFontLoaded(this.activeFont);
    if (token !== this.fontEnsureToken) {
      return;
    }
    this.fontLoadFailed = !result.loaded;
    this.cdr.markForCheck();
    this.applyStyle.emit({
      fontFamily: this.activeFont,
      fontSize: this.fontSize,
      fontColor: this.fontColor,
      backgroundColor: this.bgColor,
      backgroundStyle: this.backgroundStyle,
      position: this.position,
    });
  }

  /** One line, no control characters, bounded length. */
  private sanitizePreviewText(value: string): string {
    return value
      .replace(/[\r\n\t\v\f]+/g, " ")
      .replace(/[\u0000-\u001F\u007F]/g, "")
      .slice(0, ClippingStudioSubtitleStylePanelComponent.PREVIEW_MAX_LENGTH);
  }

  private getCaretOffset(el: HTMLElement): number {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      return 0;
    }
    const range = selection.getRangeAt(0);
    if (!el.contains(range.endContainer)) {
      return 0;
    }
    const beforeCaret = range.cloneRange();
    beforeCaret.selectNodeContents(el);
    beforeCaret.setEnd(range.endContainer, range.endOffset);
    return beforeCaret.toString().length;
  }

  private setCaretOffset(el: HTMLElement, offset: number): void {
    const selection = window.getSelection();
    if (!selection) {
      return;
    }
    const range = document.createRange();
    const textNode = el.firstChild;
    if (textNode && textNode.nodeType === Node.TEXT_NODE) {
      range.setStart(
        textNode,
        Math.min(offset, textNode.textContent?.length ?? 0),
      );
      range.collapse(true);
    } else {
      range.selectNodeContents(el);
      range.collapse(false);
    }
    selection.removeAllRanges();
    selection.addRange(range);
  }

  private scrollCaretIntoView(): void {
    const scroll = this.previewScrollRef?.nativeElement;
    const selection = window.getSelection();
    if (!scroll || !selection || selection.rangeCount === 0) {
      return;
    }
    const caret = selection.getRangeAt(0).getBoundingClientRect();
    const box = scroll.getBoundingClientRect();
    if (caret.left === 0 && caret.right === 0) {
      return;
    }
    const margin = 8;
    if (caret.right > box.right - margin) {
      scroll.scrollLeft += caret.right - box.right + margin;
    } else if (caret.left < box.left + margin) {
      scroll.scrollLeft -= box.left + margin - caret.left;
    }
  }

  /**
   * Loads the font file first, then hands it to the sample, so the previously
   * applied face stays on screen while the new one downloads. Also switches on
   * failure, so the sample and the error icon never contradict each other.
   */
  private async applyPreviewFontAfterLoad(font: string): Promise<void> {
    const token = ++this.fontEnsureToken;
    this.fontLoadFailed = false;
    const result = await this.fontService.ensureFontLoaded(font);
    if (token !== this.fontEnsureToken) {
      return;
    }
    this.fontLoadFailed = !result.loaded;
    this.previewFont = font;
    this.cdr.markForCheck();
  }

  private async refreshActiveFontLoad(): Promise<void> {
    const token = ++this.fontEnsureToken;
    this.fontLoadFailed = false;
    const result = await this.fontService.ensureFontLoaded(this.activeFont);
    if (token !== this.fontEnsureToken) {
      return;
    }
    this.fontLoadFailed = !result.loaded;
    this.cdr.markForCheck();
  }
}
